import React, { Suspense, lazy, useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { ClipboardList, Filter, Loader2, Map as MapIcon, List, X } from "lucide-react";
import { api, formatPrice, formatApiError } from "@/lib/api";
import PropertyCard from "@/components/PropertyCard";
import Pagination from "@/components/Pagination";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SearchFilterToolbar from "@/components/search/SearchFilterToolbar";
import PropertyFiltersDrawer from "@/components/search/PropertyFiltersDrawer";
import { SORT, readParams, countDrawerFilters } from "@/components/search/searchShared";
import { PHASE1_ENABLED } from "@/lib/featureFlags";

// Leaflet stays out of the initial bundle (and out of jsdom) until the map renders.
const SearchMapWorkspace = lazy(() => import("@/components/search/SearchMapWorkspace"));

const PAGE_LIMIT = 24;

function buildQuery(params, overrides = {}) {
  const qs = new URLSearchParams();
  Object.entries({ ...params, ...overrides }).forEach(([key, value]) => {
    if (value == null || value === "" || value === "any") return;
    if (Array.isArray(value)) {
      if (value.length) qs.set(key, value.join(","));
    } else {
      qs.set(key, String(value));
    }
  });
  qs.delete("view");
  return qs;
}

export default function Properties() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [mapItems, setMapItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [hoveredId, setHoveredId] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState(() => readParams(searchParams));

  const params = useMemo(() => readParams(searchParams), [searchParams]);
  const allowedViews = PHASE1_ENABLED ? ["list"] : ["list", "map", "split"];
  const defaultView = PHASE1_ENABLED ? "list" : "split";
  const requestedView = searchParams.get("view");
  const view = allowedViews.includes(requestedView) ? requestedView : defaultView;
  const page = Number(searchParams.get("page") || 1);
  const filterKey = buildQuery(params, { page: "" }).toString();

  useEffect(() => { setDraft(readParams(searchParams)); }, [searchParams]);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const qs = buildQuery(params);
      if (!qs.has("limit")) qs.set("limit", String(PAGE_LIMIT));
      // Purpose: public search only returns published inventory — sold stays hidden.
      const { data } = await api.get(`/search/properties?${qs.toString()}`);
      const list = data?.items || data?.properties || (Array.isArray(data) ? data : []);
      setItems(list);
      setTotal(Number(data?.total ?? list.length));
    } catch (requestError) {
      setItems([]);
      setTotal(0);
      setError(formatApiError(requestError) || "Properties could not be loaded. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => { fetchList(); }, [fetchList]);

  // The map needs the full filtered inventory (not one page) as lightweight pins.
  useEffect(() => {
    if (PHASE1_ENABLED || view === "list") return undefined;
    let live = true;
    const qs = buildQuery(params, { page: "", limit: "200" });
    api.get(`/search/properties?${qs.toString()}`)
      .then(({ data }) => {
        if (!live) return;
        setMapItems(data?.items || data?.properties || []);
      })
      .catch(() => { if (live) setMapItems([]); });
    return () => { live = false; };
  }, [filterKey, view]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyParams = useCallback((patch) => {
    const sp = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([key, value]) => {
      if (value == null || value === "") sp.delete(key);
      else sp.set(key, String(value));
    });
    sp.delete("page");
    setSearchParams(sp);
  }, [searchParams, setSearchParams]);

  const applyDraft = () => {
    const out = {};
    Object.entries(draft).forEach(([key, value]) => {
      if (value == null || value === "" || value === "any") return;
      if (Array.isArray(value)) {
        if (value.length) out[key] = value.join(",");
      } else {
        out[key] = String(value);
      }
    });
    if (!PHASE1_ENABLED && view !== "split") out.view = view;
    delete out.page;
    setSearchParams(out);
  };

  const reset = () => {
    setDraft({});
    if (PHASE1_ENABLED) {
      setSearchParams({});
      return;
    }
    setSearchParams(view !== "split" ? { view } : {});
  };

  const updateDraft = (patch) => setDraft((d) => ({ ...d, ...patch }));
  const toggleAmenity = (a) => {
    const list = draft.amenities || [];
    updateDraft({ amenities: list.includes(a) ? list.filter((x) => x !== a) : [...list, a] });
  };

  const setView = (next) => {
    if (PHASE1_ENABLED) {
      const sp = new URLSearchParams(searchParams);
      sp.delete("view");
      sp.delete("land_only");
      setSearchParams(sp);
      return;
    }
    const sp = new URLSearchParams(searchParams);
    if (next === "split") sp.delete("view");
    else sp.set("view", next);
    // Legacy map params no longer force land-only/verified defaults.
    sp.delete("land_only");
    setSearchParams(sp);
  };

  const onSearchArea = ({ lat, lng, radiusKm }) => {
    applyParams({ near_lat: lat.toFixed(6), near_lng: lng.toFixed(6), radius_km: String(radiusKm) });
  };

  const activeChips = useMemo(() => {
    const chips = [];
    if (params.q) chips.push({ k: "q", l: `“${params.q}”` });
    if (params.city) chips.push({ k: "city", l: params.city });
    if (params.locality) chips.push({ k: "locality", l: params.locality });
    if (params.category) chips.push({ k: "category", l: params.category.replace("_", "/") });
    if (params.listing_type) chips.push({ k: "listing_type", l: params.listing_type === "RENT" ? "For rent" : "For sale" });
    if (params.min_price) chips.push({ k: "min_price", l: `> ${formatPrice(params.min_price)}` });
    if (params.max_price) chips.push({ k: "max_price", l: `< ${formatPrice(params.max_price)}` });
    if (params.min_area) chips.push({ k: "min_area", l: `Area > ${params.min_area}` });
    if (params.max_area) chips.push({ k: "max_area", l: `Area < ${params.max_area}` });
    if (params.facing) chips.push({ k: "facing", l: `${params.facing} facing` });
    if (params.ownership_type) chips.push({ k: "ownership_type", l: params.ownership_type.replaceAll("_", " ") });
    if (params.corner_plot === "true") chips.push({ k: "corner_plot", l: "Corner plot" });
    if (params.registry_available === "true") chips.push({ k: "registry_available", l: "Registry" });
    if (params.mutation_available === "true") chips.push({ k: "mutation_available", l: "Mutation" });
    if (params.verified_documents === "true") chips.push({ k: "verified_documents", l: "Verified docs" });
    if (params.verified_owner === "true") chips.push({ k: "verified_owner", l: "Verified owner" });
    if (params.bedrooms) chips.push({ k: "bedrooms", l: `${params.bedrooms}+ BHK` });
    if (params.amenities?.length) chips.push({ k: "amenities", l: `${params.amenities.length} amenities` });
    if (params.near_lat && params.near_lng) chips.push({ k: "near_lat", l: "Map area" });
    return chips;
  }, [params]);

  const removeChip = (key) => {
    const sp = new URLSearchParams(searchParams);
    sp.delete(key);
    if (key === "near_lat") { sp.delete("near_lng"); sp.delete("radius_km"); }
    sp.delete("page");
    setSearchParams(sp);
  };

  const headline = useMemo(() => {
    const kind = params.category
      ? `${params.category.charAt(0)}${params.category.slice(1).toLowerCase().replace("_", "/")}s`.replace("Pg_hostels", "PG/Hostels")
      : "Properties";
    const status = params.listing_type === "RENT" ? "for Rent" : "for Sale";
    const where = params.locality || params.city || params.q;
    return `${kind} ${status}${where ? ` in ${where}` : ""}`;
  }, [params]);

  const sortSelect = (
    <Select value={params.sort || "newest"} onValueChange={(v) => applyParams({ sort: v })}>
      <SelectTrigger className="h-8 w-44 rounded-full border-border text-xs font-bold" data-testid="sort-trigger"><SelectValue /></SelectTrigger>
      <SelectContent>{SORT.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
    </Select>
  );

  const listHeader = (
    <div className="flex flex-wrap items-center justify-between gap-3 pb-4" data-testid="search-hero-summary">
      <div className="min-w-0">
        <h1 className="truncate font-display text-lg font-black tracking-tight text-foreground sm:text-xl">{headline}</h1>
        <p className="mt-0.5 text-xs font-semibold text-muted-foreground">
          {loading ? "Searching..." : <><span className="text-foreground">{total.toLocaleString("en-IN")}</span> {total === 1 ? "property" : "properties"}</>}
        </p>
      </div>
      {sortSelect}
    </div>
  );

  const chipsRow = activeChips.length > 0 && (
    <div className="flex flex-wrap gap-1.5 pb-4" data-testid="active-chips">
      {activeChips.map((chip) => (
        <button key={chip.k} type="button" onClick={() => removeChip(chip.k)} className="inline-flex items-center gap-1 rounded-full bg-secondary px-2.5 py-1 text-[11px] font-bold text-foreground transition hover:bg-border">
          {chip.l} <X className="h-3 w-3" />
        </button>
      ))}
      <button type="button" onClick={reset} className="rounded-full px-2.5 py-1 text-[11px] font-bold text-muted-foreground underline-offset-2 hover:text-primary hover:underline">Clear all</button>
    </div>
  );

  const cardsGrid = (cols) => (
    <div className={`grid gap-4 ${cols}`}>
      {items.map((p) => {
        const id = String(p.property_id || p.id || "");
        return (
          <div
            key={id}
            onMouseEnter={() => setHoveredId(id)}
            onMouseLeave={() => setHoveredId(null)}
            className={`rounded-2xl transition-shadow ${hoveredId === id || selectedId === id ? "ring-2 ring-primary/60 shadow-lg" : ""}`}
          >
            <PropertyCard property={p} />
          </div>
        );
      })}
    </div>
  );

  const resultsBody = loading ? (
    <div className="grid gap-4 sm:grid-cols-2">{[...Array(6)].map((_, i) => <div key={i} className="h-96 shimmer rounded-xl" />)}</div>
  ) : error ? (
    <div className="rounded-2xl border border-destructive/30 bg-destructive/5 px-6 py-14 text-center" role="alert" data-testid="search-error-state">
      <h3 className="font-display text-2xl font-black text-foreground">Properties could not be loaded</h3>
      <p className="mx-auto mt-2 max-w-xl text-sm text-muted-foreground">{error}</p>
      <Button type="button" onClick={fetchList} className="mt-5" title="Retry loading properties">Try again</Button>
    </div>
  ) : items.length === 0 ? (
    <div className="rounded-2xl border border-dashed border-border bg-secondary/50 py-16 text-center" data-testid="search-empty-state">
      <Filter className="mx-auto mb-4 h-10 w-10 text-muted-foreground" />
      <h3 className="font-display text-2xl font-black text-primary">No published property matches this search</h3>
      <p className="mx-auto mt-2 max-w-xl px-4 text-sm text-muted-foreground">Try widening your filters, or submit a request through Plots on Demand so the team can suggest published land and plot options.</p>
      <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
        <Button variant="outline" onClick={reset} data-testid="empty-reset">Reset filters</Button>
        <Link to="/plot-on-demand" className="inline-flex items-center justify-center rounded-full bg-primary px-4 py-2 text-sm font-bold text-primary-foreground shadow hover:opacity-90" data-testid="empty-plot-demand-cta"><ClipboardList className="mr-2 h-4 w-4" />Open Plots on Demand</Link>
      </div>
    </div>
  ) : null;

  const pagination = !loading && !error && items.length > 0 && (
    <Pagination
      page={page}
      limit={PAGE_LIMIT}
      total={total}
      onPageChange={(newPage) => {
        const sp = new URLSearchParams(searchParams);
        sp.set("page", String(newPage));
        setSearchParams(sp);
        document.getElementById("search-results-top")?.scrollIntoView({ behavior: "smooth" });
      }}
    />
  );

  const mapPanel = (
    <Suspense fallback={<div className="grid h-full w-full place-items-center bg-secondary/30"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>}>
      <SearchMapWorkspace
        properties={mapItems.length ? mapItems : items}
        fitKey={filterKey}
        hoveredId={hoveredId}
        selectedId={selectedId}
        onPinHover={setHoveredId}
        onPinSelect={setSelectedId}
        onSearchArea={onSearchArea}
      />
    </Suspense>
  );

  return (
    <div className="min-h-screen bg-background" data-testid="properties-page">
      <SearchFilterToolbar
        params={params}
        applyParams={applyParams}
        drawerFilterCount={countDrawerFilters(params)}
        onOpenFilters={() => setDrawerOpen(true)}
        view={view}
        onViewChange={setView}
        showViewToggle={!PHASE1_ENABLED}
      />

      {PHASE1_ENABLED || view === "list" ? (
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6" id="search-results-top" data-testid="search-results-region">
          {listHeader}
          {chipsRow}
          {resultsBody || (
            <>
              {cardsGrid("sm:grid-cols-2 xl:grid-cols-3")}
              {pagination}
            </>
          )}
        </div>
      ) : view === "map" ? (
        <div className="relative h-[calc(100vh-8rem)]" data-testid="search-map-fullscreen">{mapPanel}</div>
      ) : (
        <div className="mx-auto max-w-[1700px] lg:grid lg:grid-cols-[minmax(0,44%)_minmax(0,56%)]" data-testid="search-split-layout">
          <section className="min-w-0 px-4 py-5 sm:px-5" id="search-results-top" data-testid="search-results-region">
            {listHeader}
            {chipsRow}
            {resultsBody || (
              <>
                {cardsGrid("grid-cols-1 md:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2")}
                {pagination}
              </>
            )}
          </section>
          <section className="sticky top-[7.4rem] hidden h-[calc(100vh-7.4rem)] border-l border-border lg:block" data-testid="search-map-panel">
            {mapPanel}
          </section>
        </div>
      )}

      <PropertyFiltersDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        draft={draft}
        updateDraft={updateDraft}
        apply={applyDraft}
        reset={reset}
        total={total}
        loading={loading}
        toggleAmenity={toggleAmenity}
      />

      {/* Mobile: quick switch between list and map */}
      {!PHASE1_ENABLED && view !== "map" ? (
        <button type="button" onClick={() => setView("map")} className="fixed bottom-5 left-1/2 z-40 inline-flex -translate-x-1/2 items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-black text-primary-foreground shadow-2xl lg:hidden" data-testid="mobile-map-toggle">
          <MapIcon className="h-4 w-4" /> Map
        </button>
      ) : !PHASE1_ENABLED ? (
        <button type="button" onClick={() => setView("split")} className="fixed bottom-5 left-1/2 z-40 inline-flex -translate-x-1/2 items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-black text-primary-foreground shadow-2xl lg:hidden" data-testid="mobile-list-toggle">
          <List className="h-4 w-4" /> List
        </button>
      ) : null}
    </div>
  );
}
